import {
  useLayoutEffect,
  useRef,
  type ComponentType,
  type SVGProps,
} from 'react';
import { Compass, House, MessageCircle, Plane, User } from 'lucide-react';
import { Link, useMatch } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import HostTabButton from '@/components/molecules/HostTabButton';
import { cn } from '@/lib/utils';
import { PAGE_PATHS } from '@/routes/const';
import { firstMvp } from '@/lib/productScope';
import type { PrimaryTab } from '@/routes/navigation';

interface TabItem {
  key: PrimaryTab;
  to: string;
  labelKey: string;
  icon: ComponentType<SVGProps<SVGSVGElement>>;
  end?: boolean;
}

const MVP_TABS: TabItem[] = [
  {
    key: 'home',
    to: PAGE_PATHS.HOME,
    labelKey: 'tabs.home',
    icon: House,
    end: true,
  },
  {
    key: 'explore',
    to: PAGE_PATHS.EXPLORE,
    labelKey: 'tabs.explore',
    icon: Compass,
  },
  {
    key: 'host',
    to: PAGE_PATHS.CHAT,
    labelKey: 'tabs.host',
    icon: MessageCircle,
  },
  { key: 'trip', to: PAGE_PATHS.TRIP, labelKey: 'tabs.trip', icon: Plane },
  { key: 'my', to: PAGE_PATHS.MY, labelKey: 'tabs.my', icon: User },
];

const LEFT_TABS: TabItem[] = [
  {
    key: 'home',
    to: PAGE_PATHS.HOME,
    labelKey: 'tabs.home',
    icon: House,
    end: true,
  },
  {
    key: 'explore',
    to: PAGE_PATHS.EXPLORE,
    labelKey: 'tabs.explore',
    icon: Compass,
  },
];
const RIGHT_TABS: TabItem[] = [
  { key: 'trip', to: PAGE_PATHS.TRIP, labelKey: 'tabs.trip', icon: Plane },
  { key: 'my', to: PAGE_PATHS.MY, labelKey: 'tabs.my', icon: User },
];

function Tab({ item, activeTab }: { item: TabItem; activeTab?: PrimaryTab }) {
  const { t } = useTranslation();
  const Icon = item.icon;
  const highlightHost = firstMvp && item.to === PAGE_PATHS.CHAT;
  const match = useMatch({ path: item.to, end: item.end ?? false });
  const isActive = activeTab ? activeTab === item.key : !!match;
  return (
    <Link
      to={item.to}
      aria-current={isActive ? 'page' : undefined}
      className={cn(
        'gp-tab-link flex flex-1 flex-col items-center gap-1 py-2 text-[11px] font-medium',
        highlightHost && '-mt-5 pt-0 font-semibold',
        isActive ? 'text-gp-pink-800' : 'text-gp-ink-600',
      )}
    >
      {highlightHost ? (
        <span className="gp-host-tab grid size-14 place-items-center rounded-full bg-gp-pink-500 text-gp-ink-900 shadow-glow ring-4 ring-white">
          <Icon className="size-6" aria-hidden="true" />
        </span>
      ) : (
        <Icon className="size-6" aria-hidden="true" />
      )}
      {t(item.labelKey)}
    </Link>
  );
}

/** 하단 탭: Home · Explore · Host(중앙, 아바타 + 온라인 점) · My Trip · My (실행계획 6.2) */
export default function BottomTabBar({
  activeTab,
}: { activeTab?: PrimaryTab } = {}) {
  const { t } = useTranslation();
  const element = useRef<HTMLElement>(null);
  useLayoutEffect(() => {
    const nav = element.current!;
    const frame = nav.closest<HTMLElement>('[data-app-frame]');
    if (!frame) return;
    const measure = () => {
      const bounds = nav.getBoundingClientRect();
      const top = Math.min(
        bounds.top,
        ...Array.from(nav.querySelectorAll('a')).map(
          (link) => link.getBoundingClientRect().top,
        ),
      );
      frame.style.setProperty(
        '--gp-bottom-nav-height',
        `${Math.ceil(bounds.height)}px`,
      );
      frame.style.setProperty(
        '--gp-bottom-nav-overhang',
        `${Math.ceil(bounds.top - top) + 8}px`,
      );
    };
    measure();
    const observer =
      typeof ResizeObserver === 'undefined'
        ? undefined
        : new ResizeObserver(measure);
    observer?.observe(nav);
    return () => {
      observer?.disconnect();
      frame.style.removeProperty('--gp-bottom-nav-height');
      frame.style.removeProperty('--gp-bottom-nav-overhang');
    };
  }, []);

  return (
    <nav
      ref={element}
      data-bottom-navigation
      className="sticky bottom-0 z-30 mt-auto shrink-0 border-t border-gp-ink-900/5 bg-white/95 pb-safe backdrop-blur"
      aria-label={t('header.navigation')}
    >
      <div className="flex items-end">
        {firstMvp &&
          MVP_TABS.map((item) => (
            <Tab key={item.to} item={item} activeTab={activeTab} />
          ))}
        {!firstMvp &&
          LEFT_TABS.map((item) => (
            <Tab key={item.to} item={item} activeTab={activeTab} />
          ))}
        {!firstMvp && (
          <HostTabButton
            active={activeTab ? activeTab === 'host' : undefined}
          />
        )}
        {!firstMvp &&
          RIGHT_TABS.map((item) => (
            <Tab key={item.to} item={item} activeTab={activeTab} />
          ))}
      </div>
    </nav>
  );
}
